import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import { Card } from '@/components/ui/card';
import { CustomerAutocomplete } from '@/components/ui/customer-autocomplete';
import {
        Form,
        FormControl,
        FormField,
        FormItem,
        FormLabel,
        FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FieldList } from '@/components/ui/field-list';
import { FormSwitch } from '@/components/ui/form-switch';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import { VehicleAutocomplete } from '@/components/ui/vehicle-autocomplete';
import { useEntityForm, type EntityFormProps } from '@/hooks/useEntityForm';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

import {
        buildContractFormSubmitData,
        contractFormSchema,
        defaultContractFormValues,
        type ContractFormSubmitData,
        type ContractFormValues
} from './contract-input';

interface CustomerOption {
	customerId: string;
	firmName: string;
	categoryName?: string | null;
	categoryLimit?: number | null;
	contactName?: string | null;
	contactEmail?: string | null;
	contactPhone?: string | null;
}

interface ContractFormProps
	extends EntityFormProps<ContractFormValues, ContractFormSubmitData> {
	customerOptions: CustomerOption[];
	initialCustomerLabel?: string; // Label to show for the initially selected customer
	headerTitle: string;
	onBack?: () => void;
	contractNumber?: string; // Read-only contract number (generated on create, fetched on edit)
}
export const ContractForm = ({
	mode,
	initialData,
	onSubmit,
	isSubmitting,
	customerOptions,
	initialCustomerLabel,
	headerTitle,
	onBack,
	contractNumber
}: ContractFormProps) => {
	const { t } = useTranslation('contracts');
	const form = useEntityForm<ContractFormValues>({
		mode,
		schema: contractFormSchema,
		defaultValues: defaultContractFormValues,
		initialData
	});

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	const defaultVehicleItem = useMemo(
		() => ({
			contractVehicleId: undefined,
			vehicleId: '',
			plateNumber: '', // For display only
			isVip: false,
			rfidTag: '',
			isDeleted: false
		}),
		[]
	);

	const selectedCustomerId = form.watch('customerId');
	const selectedCustomer = useMemo(
		() =>
			customerOptions.find(
				customer => customer.customerId === selectedCustomerId
			),
		[customerOptions, selectedCustomerId]
	);

	const handleSubmit = (values: ContractFormValues) => {
		const payload = buildContractFormSubmitData(values);
		return onSubmit(payload);
	};

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				control={form.control}
				rightContent={
					<FormActionButtons
						mode={mode}
						isSubmitting={isSubmitting}
						validationErrors={form.formState.errors}
						formId="contract-form"
					/>
				}
			/>
			<Form {...form}>
				<form
					id="contract-form"
					onSubmit={form.handleSubmit(handleSubmit, errors => {
						console.log('Form validation errors:', errors);
					})}
					className="space-y-6"
				>
				<Card className="space-y-6">
					<div className="space-y-1">
						<h2 className="text-lg font-semibold">
							{t('form.sections.contractDetails.title')}
						</h2>
						<p className="text-sm text-muted-foreground">
							{t('form.sections.contractDetails.description')}
						</p>
					</div>

					{contractNumber && (
						<div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
							<div>
								<label className="text-sm font-medium leading-none">
									{t('form.contractNumber')}
								</label>
								<Input
									value={contractNumber}
									readOnly
									disabled
									className="mt-2 font-mono text-sm bg-muted"
								/>
							</div>
						</div>
					)}

					<div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						<FormField
							control={form.control}
							name="customerId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.customer')}</FormLabel>
									<FormControl>
										<CustomerAutocomplete
											value={field.value}
											onChange={customerId =>
												field.onChange(customerId)
											}
											placeholder={t(
												'form.searchCustomers'
											)}
											className="w-full"
											initialSelectedLabel={
												initialCustomerLabel ||
												selectedCustomer?.firmName ||
												undefined
											}
											autoFocus={mode === 'create'}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="beginDate"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.beginDate')}</FormLabel>
									<FormControl>
										<Input
											type="date"
											value={field.value}
											onChange={event =>
												field.onChange(
													event.target.value
												)
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="endDate"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.endDate')}</FormLabel>
									<FormControl>
										<Input
											type="date"
											value={field.value}
											onChange={event =>
												field.onChange(
													event.target.value
												)
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="amount"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.monthlyAmount')}
									</FormLabel>
									<FormControl>
										<Input
											type="number"
											min="0"
											step="0.01"
											value={field.value}
											onChange={event =>
												field.onChange(
													event.target.value
												)
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="simultaneousVehicleLimit"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.simultaneousVehicleLimit')}
									</FormLabel>
									<FormControl>
										<Input
											type="number"
											min="0"
											value={field.value ?? ''}
											onChange={event =>
												field.onChange(
													event.target.value
												)
											}
											placeholder={t(
												'form.vehicleLimit'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="reservedParkingSlots"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.reservedParkingSlots')}
									</FormLabel>
									<FormControl>
										<Input
											type="number"
											min="0"
											value={field.value ?? ''}
											onChange={event =>
												field.onChange(
													event.target.value
												)
											}
											placeholder={t(
												'form.reservedParkingSlotsPlaceholder'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="preferredLanguage"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.preferredLanguage')}
									</FormLabel>
									<FormControl>
										<SentryDropdown
											value={field.value ?? ''}
											onChange={field.onChange}
											options={[
												{ value: '', label: t('form.preferredLanguageNotSpecified') },
												{ value: 'EL', label: t('form.preferredLanguageEL') },
												{ value: 'EN', label: t('form.preferredLanguageEN') }
											]}
											searchable={false}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						<FormSwitch
							control={form.control}
							name="isAutoRenewed"
							variant="input"
							label={t('form.autoRenew')}
							enabledText={t('form.autoRenewEnabled')}
							disabledText={t('form.autoRenewDisabled')}
						/>
					</div>
				</Card>

                                <div className="space-y-4">
                                        <FieldList
                                                form={form}
                                                name="vehicles"
                                                title={t('form.assignedVehicles')}
                                                description={t('form.assignedVehiclesDescription')}
                                                emptyMessage="No vehicles assigned yet. Start by selecting a vehicle to add an entry."
                                                idField="contractVehicleId"
                                                defaultItem={defaultVehicleItem}
                                                addButtonLabel="Add New Vehicle"
                                                fields={[
							{
								name: 'vehicleId',
								isKeyField: true,
								render: ({ form, path, isNewItem }) => {
									const plateNumber = form.watch(
										`${path}.plateNumber` as any
									);
									return (
										<FormField
											control={form.control}
											name={`${path}.vehicleId` as any}
											render={({ field }) => (
												<FormItem className="md:col-span-2">
													<FormLabel className="flex items-center gap-2">
														{isNewItem && (
															<Plus className="w-4 h-4 text-muted-foreground" />
														)}
														{isNewItem
															? 'Add new vehicle'
															: 'Vehicle'}
													</FormLabel>
													<FormControl>
														<VehicleAutocomplete
															value={field.value}
															onChange={(
																vehicleId
															) => {
																field.onChange(
																	vehicleId
																);
															}}
															placeholder={
																isNewItem
																	? t(
																			'form.selectVehicle'
																		)
																	: t(
																			'form.selectVehiclePlaceholder'
																		)
															}
															enableCreateNew={
																true
															}
															initialSelectedLabel={
																plateNumber ||
																undefined
															}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									);
								}
							},
							{
								name: 'rfidTag',
								render: ({ form, path }) => (
									<FormField
										control={form.control}
										name={`${path}.rfidTag` as any}
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t('form.rfidTag')}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														value={
															field.value ?? ''
														}
														placeholder={t(
															'form.rfidTagPlaceholder'
														)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)
							},
							{
								name: 'isVip',
								render: ({ form, path }) => (
									<FormSwitch
										control={form.control}
										name={`${path}.isVip` as any}
										variant="input"
										label={t('form.vipAccess')}
										enabledText={t('form.vipEnabled')}
										disabledText={t('form.vipDisabled')}
									/>
								)
							}
						]}
					/>
				</div>
				</form>
			</Form>
		</>
	);
};
